import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { UserPlus, UserMinus, Trophy, Target, TrendingUp, ArrowLeft, Clock, Award, Plus, Mail, CreditCard, Shield, Edit2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const badgeIcons = {
  Trophy: Trophy,
  Target: Target,
  Plus: Plus,
  Award: Award
};

const UserProfile = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get(`/users/${id}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [nicknameValue, setNicknameValue] = useState('');

  const openNicknameModal = () => {
    setNicknameValue(profile.nickname || '');
    setShowNicknameModal(true);
  };

  const saveNickname = async () => {
    try {
      await apiClient.post(`/friends/nickname`, {
        friend_id: id,
        nickname: nicknameValue.trim() || null
      });
      toast.success(t('nicknameUpdated'));
      setShowNicknameModal(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update nickname');
    }
  };

  const sendFriendRequest = async () => {
    try {
      await apiClient.post(`/friends/request`, { user_id: id });
      toast.success(t('friendRequestSent'));
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const removeFriend = async () => {
    try {
      await apiClient.delete(`/friends/${id}`);
      toast.success(t('friendRemoved'));
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="app-container">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p>{language === 'de' ? 'Nutzer nicht gefunden' : 'User not found'}</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === id;

  return (
    <div className="app-container" data-testid="user-profile-page">

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <Link to="/friends" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          {t('back')}
        </Link>

        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 shadow-md border-2 border-primary/20">
                  <AvatarImage src={profile.avatar_url} alt={profile.username} className="object-cover" />
                  <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-xl">
                    {profile.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-3xl font-bold">{profile.nickname || profile.username}</h1>
                    {profile.nickname && (
                      <span className="text-sm font-normal text-muted-foreground font-mono">
                        @{profile.username}
                      </span>
                    )}
                    {profile.is_admin && <Shield className="w-5 h-5 text-blue-500" />}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {t('memberSince')} {new Date(profile.member_since).toLocaleDateString(undefined)}
                  </p>
                
                {/* Admin/Private Info */}
                {(currentUser?.is_admin || isOwnProfile) && (
                  <div className="mt-3 space-y-1">
                    {profile.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span>{profile.email}</span>
                      </div>
                    )}
                    {profile.paypal_link && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CreditCard className="w-4 h-4" />
                        <a 
                          href={profile.paypal_link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-400 hover:underline"
                        >
                          PayPal.me
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!isOwnProfile && (
                <div className="flex gap-2">
                  {profile.is_friend ? (
                    <>
                      <Button variant="outline" onClick={openNicknameModal}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        {t('editNickname')}
                      </Button>
                      <Button variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/5 hover:text-red-300" onClick={removeFriend}>
                        <UserMinus className="w-4 h-4 mr-2" />
                        {t('removeFriend')}
                      </Button>
                    </>
                  ) : (
                    <Button onClick={sendFriendRequest}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t('addFriend')}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="text-center p-4 rounded-lg bg-secondary/50">
                <p className={`text-2xl font-mono font-bold ${profile.balance < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {profile.balance >= 0 ? '+' : ''}{profile.balance.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </p>
                <p className="text-sm text-muted-foreground">{t('netProfitLoss')}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-secondary/50">
                <p className="text-2xl font-mono font-bold">{profile.total_bets}</p>
                <p className="text-sm text-muted-foreground">{t('totalBets')}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-secondary/50">
                <p className="text-2xl font-mono font-bold text-green-400">{Math.max(0, profile.won_bets)}</p>
                <p className="text-sm text-muted-foreground">{t('won')}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-secondary/50">
                <p className="text-2xl font-mono font-bold">{profile.win_rate}%</p>
                <p className="text-sm text-muted-foreground">{t('winRate')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Achievements / Badges */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              {t('achievements')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile.badges && profile.badges.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {profile.badges.map(badge => {
                  const Icon = badgeIcons[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="flex flex-col items-center p-3 rounded-lg bg-secondary/30 border border-border/50 text-center">
                      <div className="p-3 rounded-full bg-yellow-500/10 mb-2">
                        <Icon className="w-6 h-6 text-yellow-500" />
                      </div>
                      <p className="font-bold text-sm">{badge.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {t('awardedAt')} {new Date(badge.awarded_at).toLocaleDateString(undefined)}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('noAchievements')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Bets */}
        <Card>
          <CardHeader>
            <CardTitle>{t('recentBets')}</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.recent_bets && profile.recent_bets.length > 0 ? (
              <div className="space-y-2">
                {profile.recent_bets.map(bet => (
                  <Link key={bet.id} to={`/market/${bet.market_id}`}>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <p className="font-medium truncate">{bet.market_title}</p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{new Date(bet.created_at).toLocaleDateString(undefined)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={bet.position === 'yes' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}>
                          {bet.position.toUpperCase()}
                        </Badge>
                        {bet.status === 'won' ? (
                          <span className="font-mono font-bold text-green-400" title={`${language === 'de' ? 'Einsatz:' : 'Stake:'} ${(parseFloat(bet.amount) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €`}>
                            +{Math.max(0, (parseFloat(bet.payout) || parseFloat(bet.potential_payout) || 0) - (parseFloat(bet.amount) || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        ) : bet.status === 'lost' ? (
                          <span className="font-mono font-bold text-red-400" title={`${language === 'de' ? 'Einsatz:' : 'Stake:'} ${(parseFloat(bet.amount) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €`}>
                            -{(parseFloat(bet.amount) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        ) : bet.status === 'refunded' ? (
                          <span className="font-mono text-muted-foreground" title={`${language === 'de' ? 'Einsatz:' : 'Stake:'} ${(parseFloat(bet.amount) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €`}>
                            0,00 €
                          </span>
                        ) : (
                          <span className="font-mono text-yellow-500" title={bet.potential_payout > 0 ? `${language === 'de' ? 'Mögl. Gewinn:' : 'Pot. Profit:'} +${Math.max(0, (parseFloat(bet.potential_payout) || 0) - (parseFloat(bet.amount) || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2 })} €` : ''}>
                            {(parseFloat(bet.amount) || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        )}
                        <Badge variant="outline" className={
                          bet.status === 'won' ? 'text-green-400 border-green-600' :
                          bet.status === 'lost' ? 'text-red-400 border-red-600' :
                          bet.status === 'refunded' ? 'text-yellow-400 border-yellow-600' :
                          'text-muted-foreground'
                        }>
                          {t(bet.status)}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {language === 'de' ? 'Keine Wetten' : 'No bets'}
              </p>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Nickname Modal */}
      <Dialog open={showNicknameModal} onOpenChange={setShowNicknameModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('setNickname')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('nickname')} ({profile.username})</label>
              <Input
                value={nicknameValue}
                onChange={(e) => setNicknameValue(e.target.value)}
                placeholder={language === 'de' ? 'z.B. Bester Kumpel' : 'e.g. Best Buddy'}
                maxLength={30}
              />
              <p className="text-xs text-muted-foreground">
                {language === 'de' 
                  ? 'Diesen Namen siehst nur du. Leer lassen, um den Standardnamen anzuzeigen.' 
                  : 'Only you can see this name. Leave empty to show the default username.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNicknameModal(false)}>{t('cancel')}</Button>
            <Button onClick={saveNickname}>{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserProfile;
